import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import {
  ShieldCheck,
  HardHat,
  ClipboardCheck,
  Award,
  AlertTriangle,
  FileText,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const COPY = {
  en: {
    eyebrow: "Quality & Safety",
    h1: "Quality Assurance & HSE built into every shift.",
    intro:
      "Orion runs ISO-aligned QA and HSE programs across every site we operate. Documented SOPs, third-party audits, monthly KPI reports and a zero-tolerance safety culture — so our clients pass their inspections, every time.",
    qaTitle: "Quality Assurance",
    qaDesc:
      "Our QA framework follows ISO 9001 principles — documented SOPs, monthly KPI tracking, surprise audits and client business reviews.",
    qaPoints: [
      "ISO 9001-aligned SOPs for every service line",
      "Monthly KPI tracking on response time, resolution and CSAT",
      "Surprise night-shift audits by senior supervisors",
      "Quarterly business reviews with every contracted client",
      "Digital snag and reporting via WhatsApp + Admin console",
    ],
    hseTitle: "Health, Safety & Environment",
    hseDesc:
      "Our HSE program is OSHAD-aligned, Civil Defence-aware and built around UAE labour law. Every Orion team member is HSE-inducted before stepping on site.",
    hsePoints: [
      "Site-specific risk assessments and method statements (RAMS)",
      "Permit-to-Work system for hot work, height work and confined spaces",
      "PPE issued by Orion — no shortcuts, no exceptions",
      "Toolbox talks before every shift, recorded and audited",
      "Incident reporting within 4 hours, RCA within 48 hours",
      "Civil Defence and OSHAD-aligned procedures",
    ],
    certsTitle: "Certifications & alignment",
    certs: [
      "ISO 9001 (Quality Management) — aligned",
      "ISO 14001 (Environmental Management) — aligned",
      "ISO 45001 (Occupational Health & Safety) — aligned",
      "DM (Dubai Municipality) cleaning permits",
      "DEWA / ADDC / SEWA engineer registrations",
      "Civil Defence Fire & Life Safety compliance",
      "OSHAD (Abu Dhabi Occupational Safety & Health) aligned",
      "IRATA (Industrial Rope Access) for facade & high-rise",
    ],
    pillars: [
      { Icon: ShieldCheck, t: "Zero-harm culture", d: "Safety isn't a department, it's how every Orion shift starts." },
      { Icon: HardHat, t: "PPE always provided", d: "Helmets, harnesses, gloves, hi-vis, eyewear — issued by Orion, never the client's problem." },
      { Icon: ClipboardCheck, t: "Documented SOPs", d: "Every task has a step-by-step procedure — no improvisation on critical work." },
      { Icon: Award, t: "Third-party audited", d: "Independent QA & HSE auditors review our sites quarterly." },
      { Icon: AlertTriangle, t: "Incident transparency", d: "All incidents reported to client within 4 hours, full RCA within 48 hours." },
      { Icon: FileText, t: "Insured & licensed", d: "Public liability, workmen's comp and professional indemnity — all current." },
    ],
    cta: "Get a Free Quote",
    ctaSub: "Talk to us about your QA & HSE requirements — we'll structure your contract around them.",
  },
  ar: {
    eyebrow: "الجودة والسلامة",
    h1: "ضمان الجودة والصحة والسلامة في كل وردية.",
    intro:
      "تطبق أوريون برامج جودة وسلامة وفق معايير ISO في كل موقع — إجراءات موثقة، تدقيق طرف ثالث، تقارير KPI شهرية، وثقافة سلامة بلا تهاون. عملاؤنا يجتازون عمليات التفتيش في كل مرة.",
    qaTitle: "ضمان الجودة",
    qaDesc:
      "إطار الجودة لدينا متماشٍ مع ISO 9001 — إجراءات موثقة، متابعة KPI شهرية، تدقيق مفاجئ، ومراجعات أعمال ربعية.",
    qaPoints: [
      "إجراءات تشغيل قياسية موثقة لكل خط خدمة",
      "متابعة شهرية لزمن الاستجابة والحل ورضا العميل",
      "تدقيق ليلي مفاجئ من قبل المشرفين الأقدم",
      "مراجعة أعمال ربعية مع كل عميل تعاقدي",
      "تقارير رقمية عبر WhatsApp ولوحة الإدارة",
    ],
    hseTitle: "الصحة والسلامة والبيئة",
    hseDesc:
      "برنامج HSE لدينا متماشٍ مع OSHAD ومتطلبات الدفاع المدني وقانون العمل الإماراتي. كل عضو فريق يخضع لإحاطة سلامة قبل الموقع.",
    hsePoints: [
      "تقييم مخاطر وبيانات منهجية خاصة بكل موقع (RAMS)",
      "نظام تصاريح عمل للأعمال الساخنة والارتفاعات والأماكن الضيقة",
      "معدات الحماية تصدرها أوريون — بدون استثناءات",
      "محادثات سلامة قبل كل وردية، موثقة ومدققة",
      "إبلاغ الحوادث خلال 4 ساعات، تحليل سبب الجذر خلال 48 ساعة",
      "إجراءات متماشية مع الدفاع المدني وOSHAD",
    ],
    certsTitle: "الشهادات والمواءمة",
    certs: [
      "ISO 9001 (إدارة الجودة) — متماشٍ",
      "ISO 14001 (إدارة بيئية) — متماشٍ",
      "ISO 45001 (السلامة المهنية) — متماشٍ",
      "تصاريح بلدية دبي للنظافة",
      "تسجيلات مهندسي DEWA / ADDC / SEWA",
      "امتثال الدفاع المدني للسلامة من الحريق",
      "متماشٍ مع OSHAD أبوظبي",
      "IRATA للوصول بالحبال والواجهات",
    ],
    pillars: [
      { Icon: ShieldCheck, t: "ثقافة لا ضرر", d: "السلامة ليست قسماً — هي كيف تبدأ كل وردية." },
      { Icon: HardHat, t: "معدات حماية دائماً", d: "خوذ، أحزمة، قفازات — كلها من أوريون، ليست مشكلة العميل." },
      { Icon: ClipboardCheck, t: "إجراءات موثقة", d: "كل مهمة لها إجراء مفصل — لا ارتجال في الأعمال الحرجة." },
      { Icon: Award, t: "تدقيق طرف ثالث", d: "مدققون مستقلون يراجعون مواقعنا ربعياً." },
      { Icon: AlertTriangle, t: "شفافية الحوادث", d: "كل حادث يبلغ للعميل خلال 4 ساعات، تحليل كامل خلال 48 ساعة." },
      { Icon: FileText, t: "مؤمنون ومرخصون", d: "تأمين مسؤولية عامة وتعويض عمال — جميعها سارية." },
    ],
    cta: "احصل على عرض سعر مجاني",
    ctaSub: "تحدث معنا عن متطلبات الجودة والسلامة — سنبني العقد حولها.",
  },
};

export default function QualityAndSafety() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  return (
    <div data-testid="quality-safety-page">
      <SEO
        title={`Quality Assurance & HSE | ${COMPANY.brand}`}
        description="Orion FM runs ISO 9001 / 14001 / 45001-aligned Quality and HSE programs across every site — documented SOPs, third-party audits, OSHAD-aligned safety. Zero-harm culture."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt="Quality & Safety"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        </div>
        <div className="container-orion relative py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              {c.eyebrow}
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              {c.h1}
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">{c.intro}</p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-orion">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.pillars.map((p, i) => {
              const Icon = p.Icon;
              return (
                <div
                  key={i}
                  data-testid={`qa-pillar-${i}`}
                  className="bg-slate-50 border border-slate-200 rounded-2xl p-7 card-hover"
                >
                  <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-slate-900">{p.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QA + HSE deep dive */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10">
            <div className="text-eyebrow text-blue-700">{c.qaTitle}</div>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 leading-tight">
              {c.qaTitle}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{c.qaDesc}</p>
            <ul className="mt-6 space-y-3">
              {c.qaPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700 leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10">
            <div className="text-eyebrow text-blue-700">{c.hseTitle}</div>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 leading-tight">
              {c.hseTitle}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{c.hseDesc}</p>
            <ul className="mt-6 space-y-3">
              {c.hsePoints.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <HardHat className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700 leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Certs */}
      <section className="py-20 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">{c.certsTitle}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              {c.certsTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.certs.map((cert, i) => (
              <div
                key={i}
                data-testid={`qa-cert-${i}`}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-start gap-3"
              >
                <Sparkles className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                <span className="text-sm text-slate-700 leading-snug">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950 text-white">
        <div className="container-orion text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-medium leading-tight">
            {c.ctaSub}
          </h2>
          <Link
            to="/contact"
            data-testid="qa-cta-quote"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
          >
            {c.cta}
            <ArrowRight className="w-4 h-4 rtl-flip" />
          </Link>
        </div>
      </section>
    </div>
  );
}
